import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { TIMELINE } from "@/data/timeline";
import { TIMELINE_TR } from "@/data/i18n/content";
import { MaskLines, Reveal } from "@/components/MaskLines";
import { SupportForm } from "@/components/SupportForm";
import { locById, useLang } from "@/lib/i18n";

const Photos = ({ photos, id }) => (
  <div className="mt-6 grid gap-4" data-testid={`photos-${id}`}>
    {photos.map((p, k) => (
      <figure key={p.src} className="tl-photo" data-testid={`photo-${id}-${k}`}>
        <img src={p.src} alt={p.caption} loading="lazy" style={{ objectPosition: p.position ?? "center" }} />
        <figcaption className="mono text-[11px] uppercase tracking-[0.18em]">{p.caption}</figcaption>
      </figure>
    ))}
  </div>
);

const Entry = ({ e: raw, i }) => {
  const { t, lang } = useLang();
  const loc = locById(raw, TIMELINE_TR, lang);
  const e = { ...loc, photos: raw.photos?.map((p, k) => ({ ...p, caption: loc.captions?.[k] ?? p.caption })) };
  return (
  <div className="relative grid gap-6 pl-10 md:grid-cols-2 md:gap-16 md:pl-0" data-testid={`timeline-entry-${e.id}`}>
    <span className={`tl-dot ${e.current ? "tl-dot-now" : ""}`} />
    <Reveal className={`md:row-start-1 ${i % 2 ? "md:col-start-2 md:pl-14" : "md:col-start-1 md:pr-14 md:text-right"}`}>
      <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--green)]">{e.place}</p>
      <p className="font-display mt-2 text-5xl leading-none md:text-6xl">{e.year}</p>
    </Reveal>
    <Reveal delay={0.1} className={`md:row-start-1 ${i % 2 ? "md:col-start-1 md:pr-14" : "md:col-start-2 md:pl-14"}`}>
      <div className="tl-card">
        <h3 className="font-display text-2xl md:text-3xl">{e.title}</h3>
        <p className="mt-3 text-sm leading-relaxed text-[var(--muted)] md:text-base">{e.body}</p>
        {e.tasks && (
          <ul className="mt-5 flex flex-wrap gap-2">
            {e.tasks.map((task) => <li key={task} className="rounded-full border border-white/15 px-3 py-1 text-xs">{task}</li>)}
          </ul>
        )}
        {e.badge && (
          <a href={e.link} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 rounded-full border border-[var(--green)] px-4 py-2 text-sm text-[var(--green)] transition-colors hover:bg-[var(--green)] hover:text-[#0d0c0a]" data-testid="trademark-link">
            {e.badge} · {t("proj.impi")} <ArrowUpRight size={15} />
          </a>
        )}
        {e.photos && <Photos photos={e.photos} id={e.id} />}
      </div>
    </Reveal>
  </div>
  );
};

const Invite = () => {
  const { t } = useLang();
  return (
  <Reveal className="relative mt-24">
    <div className="invite-arch" data-testid="timeline-invite">
      <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{t("proj.next")}</p>
      <h2 className="font-display mt-4 text-4xl leading-[1.05] sm:text-5xl lg:text-6xl">
        {t("proj.door1")} <em>{t("proj.door2")}</em>
      </h2>
      <p className="mx-auto mt-5 max-w-xl text-sm text-[var(--muted)] md:text-base">
        {t("proj.inviteBody")}
      </p>
      <div className="mx-auto mt-10 max-w-2xl">
        <SupportForm />
      </div>
      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link to="/explorar?mode=3d" className="btn-ghost" data-testid="invite-explore-btn">{t("proj.walk")}</Link>
      </div>
    </div>
  </Reveal>
  );
};

export const ProjectSection = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 70%", "end 60%"] });
  const scaleY = useSpring(scrollYProgress, { stiffness: 80, damping: 24 });
  return (
    <section id="proyecto" className="px-5 pb-28 pt-28 md:px-10" data-testid="project-page">
      <div className="mx-auto max-w-6xl">
        <p className="mono text-[11px] uppercase tracking-[0.22em] text-[var(--muted)]">{t("proj.kicker")}</p>
        <MaskLines as="h2" className="font-display mt-4 text-4xl leading-[0.95] sm:text-5xl lg:text-6xl" lines={[t("proj.l1"), <em key="e">{t("proj.l2")}</em>]} />
        <p className="mt-6 max-w-2xl text-sm text-[var(--muted)] md:text-base">
          {t("proj.intro")}
        </p>
        <div ref={ref} className="relative mt-20 space-y-20 md:space-y-28">
          <div className="tl-line" />
          <motion.div className="tl-line tl-line-fill" style={{ scaleY }} />
          {TIMELINE.map((e, i) => <Entry key={e.id} e={e} i={i} />)}
        </div>
        <Invite />
      </div>
    </section>
  );
};
